import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { DAY_OF_MONTH_OPTIONS, DAY_OF_WEEK_OPTIONS, MONTH_OPTIONS } from '../../../../../constants/calendar-anchor.constants';
import { CesTable, PeriodicityMode } from '../../../../../types/table.interface';
import { createUniqueNameValidator } from '../../../../../utils/validators';
import { CesDialogFooterComponent } from '../../../../ui/dialog-footer/dialog-footer.component';
import { CesHourpickerComponent } from '../../../../ui/inputs/ces-hourpicker.component';

type TableDialogMode = 'create' | 'edit' | 'duplicate';

interface TableDialogData {
  readonly mode: TableDialogMode;
  readonly table?: CesTable;
  readonly existingTableNames?: string[];
}

interface TableFormControls {
  name: FormControl<string>;
  description: FormControl<string>;
  periodicity: FormControl<string | null>;
  startTimePeriod: FormControl<string | null>;
  dayOfWeek: FormControl<number | null>;
  dayOfMonth: FormControl<number | null>;
  month: FormControl<number | null>;
  chronologicalOrder: FormControl<boolean>;
}

const INTERVAL_UNIT_WEEKS = 4;
const INTERVAL_UNIT_MONTHS = 5;
const INTERVAL_UNIT_YEARS = 6;

export type CreateTableDialogResult = Partial<CesTable> & { id?: number };

interface PeriodicityOption {
  readonly label: string;
  readonly value: string;
  readonly mode: PeriodicityMode;
  readonly periodicityValue: number | null;
  readonly periodicityUnit: number | null;
}

function hasMonthAnchor(option: PeriodicityOption | null | undefined): boolean {
  if (option?.mode !== 3) return false;
  if (option.periodicityUnit === INTERVAL_UNIT_YEARS) return true;
  return option.periodicityUnit === INTERVAL_UNIT_MONTHS && (option.periodicityValue ?? 0) > 1;
}

const PERIODICITY_OPTIONS: PeriodicityOption[] = [
  { label: 'tables.periodicity.perMeasure', value: '1', mode: 1, periodicityValue: null, periodicityUnit: null },
  { label: 'tables.periodicity.perCampaign', value: '2', mode: 2, periodicityValue: null, periodicityUnit: null },
  { label: 'tables.periodicity.interval.1min', value: '3-1-1', mode: 3, periodicityValue: 1, periodicityUnit: 1 },
  { label: 'tables.periodicity.interval.5min', value: '3-5-1', mode: 3, periodicityValue: 5, periodicityUnit: 1 },
  { label: 'tables.periodicity.interval.10min', value: '3-10-1', mode: 3, periodicityValue: 10, periodicityUnit: 1 },
  { label: 'tables.periodicity.interval.15min', value: '3-15-1', mode: 3, periodicityValue: 15, periodicityUnit: 1 },
  { label: 'tables.periodicity.interval.30min', value: '3-30-1', mode: 3, periodicityValue: 30, periodicityUnit: 1 },
  { label: 'tables.periodicity.interval.1h', value: '3-1-2', mode: 3, periodicityValue: 1, periodicityUnit: 2 },
  { label: 'tables.periodicity.interval.2h', value: '3-2-2', mode: 3, periodicityValue: 2, periodicityUnit: 2 },
  { label: 'tables.periodicity.interval.3h', value: '3-3-2', mode: 3, periodicityValue: 3, periodicityUnit: 2 },
  { label: 'tables.periodicity.interval.4h', value: '3-4-2', mode: 3, periodicityValue: 4, periodicityUnit: 2 },
  { label: 'tables.periodicity.interval.6h', value: '3-6-2', mode: 3, periodicityValue: 6, periodicityUnit: 2 },
  { label: 'tables.periodicity.interval.12h', value: '3-12-2', mode: 3, periodicityValue: 12, periodicityUnit: 2 },
  { label: 'tables.periodicity.interval.1day', value: '3-1-3', mode: 3, periodicityValue: 1, periodicityUnit: 3 },
  { label: 'tables.periodicity.interval.1week', value: '3-1-4', mode: 3, periodicityValue: 1, periodicityUnit: 4 },
  { label: 'tables.periodicity.interval.1month', value: '3-1-5', mode: 3, periodicityValue: 1, periodicityUnit: 5 },
  { label: 'tables.periodicity.interval.2months', value: '3-2-5', mode: 3, periodicityValue: 2, periodicityUnit: 5 },
  { label: 'tables.periodicity.interval.3months', value: '3-3-5', mode: 3, periodicityValue: 3, periodicityUnit: 5 },
  { label: 'tables.periodicity.interval.4months', value: '3-4-5', mode: 3, periodicityValue: 4, periodicityUnit: 5 },
  { label: 'tables.periodicity.interval.6months', value: '3-6-5', mode: 3, periodicityValue: 6, periodicityUnit: 5 },
  { label: 'tables.periodicity.interval.1year', value: '3-1-6', mode: 3, periodicityValue: 1, periodicityUnit: 6 },
];

@Component({
  selector: 'ces-create-table-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    InputTextModule,
    Textarea,
    Select,
    RadioButtonModule,
    CesDialogFooterComponent,
    CesHourpickerComponent,
    TooltipModule,
  ],
  templateUrl: './create-table-dialog.component.html',
  styleUrl: './create-table-dialog.component.scss',
})
export class CreateTableDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<TableDialogData>>(DynamicDialogConfig);
  private readonly translateService = inject(TranslocoService);

  private readonly mode = this.config.data?.mode ?? 'create';
  private readonly table = this.config.data?.table;
  private readonly existingTableNames = this.config.data?.existingTableNames ?? [];

  readonly form = this.createForm();

  private readonly nameStatus = toSignal(this.form.controls.name.statusChanges, {
    initialValue: this.form.controls.name.status,
  });
  private readonly periodicityChanges = toSignal(this.form.controls.periodicity.valueChanges, {
    initialValue: this.form.controls.periodicity.value,
  });
  private readonly submitTrigger = signal(0);

  readonly periodicityOptions = computed(() =>
    PERIODICITY_OPTIONS.map(opt => ({
      ...opt,
      translatedLabel: this.translateService.translate(opt.label),
    })),
  );

  readonly startTimeEnabled = computed(() => {
    const periodicityValue = this.periodicityChanges();
    if (!periodicityValue) return false;
    const option = PERIODICITY_OPTIONS.find(o => o.value === periodicityValue);
    return option?.mode === 3;
  });

  private readonly selectedIntervalOption = computed(() => {
    const option = PERIODICITY_OPTIONS.find(o => o.value === this.periodicityChanges());
    return option?.mode === 3 ? option : null;
  });

  private readonly selectedIntervalUnit = computed(() => this.selectedIntervalOption()?.periodicityUnit ?? null);

  readonly showDayOfWeek = computed(() => this.selectedIntervalUnit() === INTERVAL_UNIT_WEEKS);
  readonly showDayOfMonth = computed(() => {
    const unit = this.selectedIntervalUnit();
    return unit === INTERVAL_UNIT_MONTHS || unit === INTERVAL_UNIT_YEARS;
  });
  readonly showMonth = computed(() => hasMonthAnchor(this.selectedIntervalOption()));

  readonly dayOfWeekOptions = DAY_OF_WEEK_OPTIONS.map(o => ({ value: o.id, label: this.translateService.translate(o.name) }));
  readonly dayOfMonthOptions = DAY_OF_MONTH_OPTIONS.map(o => ({ value: o.id, label: o.name }));
  readonly monthOptions = MONTH_OPTIONS.map(o => ({ value: o.id, label: this.translateService.translate(o.name) }));

  readonly nameInvalid = computed(() => {
    this.nameStatus();
    this.submitTrigger();
    return this.form.controls.name.invalid && this.form.controls.name.dirty;
  });
  readonly nameNotUnique = computed(() => {
    this.nameStatus();
    this.submitTrigger();
    return this.form.controls.name.hasError('notUnique') && this.form.controls.name.dirty;
  });
  readonly periodicityInvalid = computed(() => {
    this.periodicityChanges();
    this.submitTrigger();
    return this.form.controls.periodicity.invalid && this.form.controls.periodicity.touched;
  });

  private createForm(): FormGroup<TableFormControls> {
    const name = this.getInitialName();
    const description = this.table?.description ?? '';
    const periodicity = this.getInitialPeriodicity();
    const startTimePeriod = (this.table?.startTimePeriod ?? '00:00').substring(0, 5);
    const chronologicalOrder = this.table?.chronologicalOrder ?? false;

    const initialOption = periodicity ? PERIODICITY_OPTIONS.find(o => o.value === periodicity) : null;
    const startTimeDisabled = initialOption?.mode !== 3;

    const form = new FormGroup<TableFormControls>({
      name: new FormControl(name, {
        nonNullable: true,
        validators: [
          Validators.required,
          createUniqueNameValidator(this.existingTableNames, this.mode === 'edit' ? this.table?.name : undefined),
        ],
      }),
      description: new FormControl(description, { nonNullable: true }),
      periodicity: new FormControl<string | null>(periodicity, { validators: [Validators.required] }),
      startTimePeriod: new FormControl<string | null>({ value: startTimePeriod, disabled: startTimeDisabled }),
      dayOfWeek: new FormControl<number | null>(this.table?.dayOfWeek ?? 1),
      dayOfMonth: new FormControl<number | null>(this.table?.dayOfMonth ?? 1),
      month: new FormControl<number | null>(this.table?.month ?? 1),
      chronologicalOrder: new FormControl(chronologicalOrder, { nonNullable: true }),
    });

    form.controls.periodicity.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const option = PERIODICITY_OPTIONS.find(o => o.value === value);
      if (option?.mode !== 3) {
        form.controls.startTimePeriod.setValue(null);
        form.controls.startTimePeriod.disable();
      } else {
        form.controls.startTimePeriod.enable();
        if (!form.controls.startTimePeriod.value) {
          form.controls.startTimePeriod.setValue('00:00');
        }
      }
    });

    return form;
  }

  private getInitialName(): string {
    if (this.mode === 'duplicate' && this.table) {
      const copySuffix = this.translateService.translate('common.labels.copySuffix');
      const baseName = this.table.name;
      const newName = `${baseName} - ${copySuffix}`;
      return newName.length > 30 ? newName.substring(0, 30) : newName;
    }
    return this.table?.name ?? '';
  }

  private getInitialPeriodicity(): string | null {
    if (!this.table) return '2';

    const { periodicity, periodicityValue, periodicityUnit } = this.table;
    if (!periodicity) return null;

    if (periodicity === 1) return '1';
    if (periodicity === 2) return '2';
    if (periodicity === 3 && periodicityValue && periodicityUnit) {
      return `3-${periodicityValue}-${periodicityUnit}`;
    }

    return null;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      for (const control of Object.values(this.form.controls) as AbstractControl[]) {
        control.markAsDirty();
      }
      this.submitTrigger.update(v => v + 1);
      return;
    }

    const formValue = this.form.getRawValue();
    const periodicityOption = PERIODICITY_OPTIONS.find(o => o.value === formValue.periodicity);
    const intervalUnit = periodicityOption?.mode === 3 ? periodicityOption.periodicityUnit : null;

    const result: Partial<CesTable> = {
      name: formValue.name.trim(),
      description: formValue.description,
      periodicity: periodicityOption?.mode ?? null,
      periodicityValue: periodicityOption?.periodicityValue ?? null,
      periodicityUnit: periodicityOption?.periodicityUnit ?? null,
      startTimePeriod: periodicityOption?.mode === 3 ? formValue.startTimePeriod : null,
      dayOfWeek: intervalUnit === INTERVAL_UNIT_WEEKS ? formValue.dayOfWeek : null,
      dayOfMonth: intervalUnit === INTERVAL_UNIT_MONTHS || intervalUnit === INTERVAL_UNIT_YEARS ? formValue.dayOfMonth : null,
      month: hasMonthAnchor(periodicityOption) ? formValue.month : null,
      chronologicalOrder: formValue.chronologicalOrder,
    };

    if (this.mode === 'edit' && this.table) {
      this.dialogRef.close({ ...result, id: this.table.id });
    } else {
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
